import { sendNotification, ViewCustomAction, ViewCustomActionArgs } from '@msdk/common'
import {
  DefaultActionType,
  Entity,
  EntityMetadata,
  EntityModel,
  EntityOperation,
  OptionsetValue,
  readBaseMetadataByName,
  readEntityModelByName,
  ServerOperationResult,
} from '@msdk/definitions'
import { ActionConfirmationModal, messagesFromEntityOperation, resolveConfig } from '@msdk/forms'
import React from 'react'

export class ActivateAction extends ViewCustomAction {
  private title: string
  private body: string
  private success: string
  private error: string
  private confirmationLabel: string
  private confirmNeeded = false

  constructor(args: ViewCustomActionArgs) {
    super(args)
    this.title = this.translate('VIEW_ACTION_TITLE_ACTIVATION', 'Activate items')
    this.body = this.translate('VIEW_ACTION_DESCRIPTION_ACTIVATION', 'Are you sure?')
    this.success = this.translate('VIEW_ACTION_SUCCESS_ACTIVATION', 'Items were activated successfully')
    this.error = this.translate('VIEW_ACTION_ERROR_ACTIVATION', 'Items activation failed')
    this.confirmationLabel = this.translate('VIEW_ACTION_ACTIVATION_CONFIRMATION_LABEL', 'Activate')
  }

  public onInit(): Promise<void> {
    const config = resolveConfig(this.config)

    const titleIdml = config['TITLE_IDML']
    const titleDesc = config['TITLE_DESCRIPTION']
    if (titleIdml && titleDesc) this.title = this.translate(titleIdml, titleDesc)

    const bodyIdml = config['BODY_IDML']
    const bodyDesc = config['BODY_DESCRIPTION']
    if (bodyIdml && bodyDesc) this.body = this.translate(bodyIdml, bodyDesc)

    const successIdml = config['SUCCESS_IDML']
    const successDesc = config['SUCCESS_DESCRIPTION']
    if (successIdml && successDesc) this.success = this.translate(successIdml, successDesc)

    const errorIdml = config['ERROR_IDML']
    const errorDesc = config['ERROR_DESCRIPTION']
    if (errorIdml && errorDesc) this.error = this.translate(errorIdml, errorDesc)

    const confirmIdml = config['CONFIRM_IDML']
    const confirmDesc = config['CONFIRM_DESCRIPTION']
    if (confirmIdml && confirmDesc) this.confirmationLabel = this.translate(confirmIdml, confirmDesc)

    this.confirmNeeded = config['CONFIRM_NEEDED']?.toLowerCase() === 'true'

    return Promise.resolve()
  }

  async onClick(): Promise<void> {
    if (this.confirmNeeded) {
      await this.confirmRequest()
    } else {
      await this.activate()
    }
  }

  confirmRequest = async () => {
    const modal = this.componentModal.createModal(
      <ActionConfirmationModal
        title={this.title}
        body={this.body}
        onConfirm={this.activate}
        confirmationLabel={this.confirmationLabel}
        type={DefaultActionType.Activate}
      />,
      () => {
        //do nothing
      },
    )

    modal.open()
  }

  activate = async () => {
    const promises: Promise<EntityOperation>[] = []
    this.selectedRows.forEach((row) => {
      const baseMetadata = readBaseMetadataByName(row.entityName) as EntityMetadata
      const model = readEntityModelByName(row.entityName) as EntityModel
      const toUpdate: Entity = {
        entityName: row.entityName,
        id: row.id,
        isPersisted: row.isPersisted,
        parentId: row.parentId,
        internalId: row.internalId,
        isInCreation: row.isInCreation,
        fields: {
          ENTITY_STATUS: new OptionsetValue('ACTIVE', 'ENTITY_STATUS'),
        },
        canActivate: row.canActivate,
        canDeactivate: row.canDeactivate,
        canUpdate: row.canUpdate,
        canDelete: row.canDelete,
      }

      if (model.extendedBy != null && model.extendedBy != '') {
        toUpdate.fields['EXTENDED_BY'] = row.fields['EXTENDED_BY']
        toUpdate.fields['EXTENDED_BY_PARENTID'] = row.fields['EXTENDED_BY_PARENTID']
      }

      const promise = this.apiClient.entity.forceSaveEntities(baseMetadata.msProvider, model, [toUpdate])
      promises.push(promise)
    })

    const results = await Promise.all(promises)
    const failures = results.filter((r) => r.result == ServerOperationResult.Failure)
    if (failures.length) {
      failures.forEach((f) => {
        const serverMessages = messagesFromEntityOperation(f)
        if (serverMessages.length) {
          const translated = serverMessages.reduce(
            (acc, curr) => (acc += `\n${this.translate(curr.idml, curr.description)}`),
            '',
          )
          sendNotification({ message: translated, status: 'error' })
        } else {
          sendNotification({ message: this.error, status: 'error' })
        }
      })
    } else {
      sendNotification({ message: this.success, status: 'success' })
    }

    this.reload()
  }

  isDisabled(): boolean {
    return this.selectedRows.length === 0
  }

  isVisible(): boolean {
    return this.acl?.canActivate ?? false
  }
}
